import { motion, AnimatePresence } from 'framer-motion';
import { useEffect } from 'react';
import { useScrollPrevention } from '../hooks/useScrollPrevention';

interface SuccessAnimationProps {
  isVisible: boolean;
  message: string;
  onAnimationComplete?: () => void;
  duration?: number;
}

const SuccessAnimation: React.FC<SuccessAnimationProps> = ({
  isVisible,
  message,
  onAnimationComplete,
  duration = 2000
}) => {
  // Modal açıkken scroll'u engelle
  useScrollPrevention(isVisible);
  
  // Ek scroll engelleme - useScrollPrevention yeterli değilse
  useEffect(() => {
    if (isVisible) {
      // Body'ye overflow hidden ekle
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // Cleanup: original overflow'u geri yükle
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isVisible]);
  
  useEffect(() => {
    if (isVisible && onAnimationComplete) {
      const timer = setTimeout(() => {
        onAnimationComplete();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [isVisible, onAnimationComplete, duration]);

  // Satır atlamalarını yönetmek için mesajı işle
  const formatMessage = (text: string) => {
    // Satır atlamaları varsa bunları <br /> olarak böl
    const parts = text.split(/\n|<br\s*\/?>/);
    
    return parts.map((part, index) => (
      <span key={index}>
        {part}
        {index < parts.length - 1 && <br />}
      </span>
    ));
  };

  // Çemberin yay animasyonu
  const circleVariants = {
    hidden: {
      opacity: 0,
      pathLength: 0,
    },
    visible: {
      opacity: 1,
      pathLength: 1.01,
      transition: {
        pathLength: { type: "spring", duration: 1.5, bounce: 0 },
        opacity: { duration: 0.01 }
      }
    }
  };

  // Tik işaretinin animasyonu
  const checkVariants = {
    hidden: {
      opacity: 0,
      pathLength: 0
    },
    visible: {
      opacity: 1,
      pathLength: 1,
      transition: {
        pathLength: { type: "spring", duration: 1.5, bounce: 0, delay: 0.3 },
        opacity: { duration: 0.01, delay: 0.3 }
      }
    }
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 flex items-center justify-center z-50 bg-black/20 dark:bg-black/40 backdrop-blur-sm"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ 
              type: "spring", 
              stiffness: 300, 
              damping: 25 
            }}
            className="bg-white dark:bg-gray-800 rounded-xl p-6 max-w-md w-full shadow-2xl mx-4 text-center flex flex-col items-center"
          >
            <div className="w-32 h-32 mb-6 relative">
              <svg viewBox="0 0 52 52" className="w-full h-full">
                {/* Dış çember animasyonu */}
                <motion.circle
                  cx="26"
                  cy="26"
                  r="24"
                  fill="transparent"
                  stroke="#4ade80" // Yeşil renk
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  variants={circleVariants}
                  initial="hidden"
                  animate="visible"
                />
                {/* Tik işareti animasyonu */}
                <motion.path
                  d="M14,27 L22,35 L38,15"
                  fill="transparent"
                  stroke="#4ade80" // Yeşil renk
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  variants={checkVariants}
                  initial="hidden"
                  animate="visible"
                />
              </svg>
            </div>
            
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="text-xl font-semibold text-gray-900 dark:text-white mb-4"
            >
              Başarılı!
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9 }}
              className="text-gray-600 dark:text-gray-300"
            >
              {formatMessage(message)}
            </motion.p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SuccessAnimation; 